/**
 * What an organization looks like, and the one property that makes offering it safe.
 *
 * Only `--brand` is ever written at runtime. Every accent the console paints - text, links, the
 * primary fill, rules, hovers and tints - is mixed from it in CSS toward the palette's own ink, so a
 * reseller choosing a color cannot produce an illegible console. That is a measurement rather than a
 * hope: `styles.test.ts` runs all six offered swatches through all four palettes in both directions.
 *
 * It follows that the list here is closed. A color picker would end the guarantee while looking like
 * a generosity, which is why the server refuses any hex outside this list and why the screen offers
 * swatches rather than an input.
 *
 * Pure, as every decision in this SPA is (D-186): the screen is layout, and this is what is tested.
 */

export interface BrandSwatch {
  /** The stored value. Must equal `BrandingSwatches.Offered` on the server, which is authoritative. */
  value: string
  name: string
}

/**
 * The six, in the server's own order.
 *
 * Duplicated from `BrandingSwatches.Offered` rather than fetched, so the screen paints before a round
 * trip - and reconciled against the server's list by `GET /api/v1/branding/swatches`, which the page
 * reads and compares. A copy that can drift is only acceptable when something notices it has.
 */
export const BRAND_SWATCHES: readonly BrandSwatch[] = [
  { value: '#0088b0', name: 'Process cyan' },
  { value: '#1f4e79', name: 'Navy' },
  { value: '#0e7c5a', name: 'Green' },
  { value: '#8a3ffc', name: 'Violet' },
  { value: '#a8410f', name: 'Rust' },
  { value: '#2d2b2b', name: 'Ink' }
]

/** What an organization that has chosen nothing is painted with. */
export const DEFAULT_ACCENT = '#0088b0'

export function isOfferedAccent(value: string | null | undefined): boolean {
  if (!value) return false
  const normalized = value.trim().toLowerCase()
  return BRAND_SWATCHES.some((swatch) => swatch.value === normalized)
}

/** The shape the anonymous read answers with. Four values and nothing else about the organization. */
export interface PublicBranding {
  brandAccent: string
  hasBrandMark: boolean
  reportFooter: string | null
  configured: boolean
}

/** The same, plus the one field only somebody signed in may see. */
export interface TenantBranding extends PublicBranding {
  signInDomain: string | null
}

/**
 * The minimum an element has to offer for a brand to be applied to it.
 *
 * Typed this narrowly so the applying function is testable without a DOM, which is the whole point of
 * this file existing beside the screen rather than inside it.
 */
export interface StyleTarget {
  style: {
    setProperty(property: string, value: string): void
    removeProperty(property: string): void
  }
}

/**
 * Writes the organization's color, and only that.
 *
 * A null accent removes the property rather than writing the default, so the stylesheet's own value
 * is what paints - which keeps "the deployment default" in one place instead of two that can
 * disagree. An accent the console does not offer is ignored for the same reason the server refuses
 * it: a value nobody measured is a value nobody can promise is readable.
 *
 * It returns what it actually applied, so a caller can tell "your color is showing" from "your
 * color was not one of ours" without inspecting the DOM.
 */
export function applyBrand(target: StyleTarget, accent: string | null | undefined): string | null {
  if (!accent || !isOfferedAccent(accent)) {
    target.style.removeProperty('--brand')
    return null
  }

  const normalized = accent.trim().toLowerCase()
  target.style.setProperty('--brand', normalized)
  return normalized
}

/**
 * How the sign-in page asks who it is being shown to.
 *
 * By slug when somebody has named their organization, and by subdomain before they have: an MSP whose
 * customers each reach the console on their own subdomain wants the right mark on the page before
 * anybody types.
 *
 * What is sent is the **leftmost label** of the host, not the whole host. An organization claims a
 * subdomain of whatever zone this deployment is already served on, so the rest of the host is the
 * deployment's rather than theirs and is the same for everybody - matching on it would be matching on
 * a constant. It also means nothing here has to know what that zone is, which is what keeps this
 * working on a deployment at any domain without a setting naming it.
 *
 * A bare host with no dot is dropped - `localhost`, or a machine name on an internal network. Its
 * single label is the name of a machine rather than an organization's claim on a subdomain, and
 * sending it would be a request that could only ever answer with the defaults.
 */
export interface BrandLookup {
  slug?: string
  domain?: string
}

export function brandLookup(hostname: string, slug?: string | null): BrandLookup | null {
  const trimmed = slug?.trim().toLowerCase() ?? ''
  if (trimmed) return { slug: trimmed }

  const host = hostname.trim().toLowerCase()
  if (!host.includes('.') || host === '127.0.0.1') return null
  return { domain: host.split('.')[0]! }
}

/** The query string for a lookup, so the client and the tests agree about how it is spelled. */
export function brandQuery(lookup: BrandLookup): string {
  const parameters = new URLSearchParams()
  if (lookup.slug) parameters.set('slug', lookup.slug)
  if (lookup.domain) parameters.set('domain', lookup.domain)
  return parameters.toString()
}

/**
 * The limits, mirrored from the server so somebody is told before a 64 KB upload rather than after.
 *
 * The server is the authority and refuses the same things; this exists to save a round trip and a
 * confusing error, not to enforce anything. A browser is not a place to enforce authority.
 */
export const MAXIMUM_MARK_BYTES = 64 * 1024
export const MAXIMUM_FOOTER_LENGTH = 200
const MARK_TYPES = ['image/png', 'image/svg+xml']

export function markProblem(file: { type: string; size: number } | null): string | null {
  if (!file) return 'Choose a PNG or SVG.'
  if (!MARK_TYPES.includes(file.type.split(';')[0]!.trim().toLowerCase())) return 'A mark is a PNG or an SVG.'
  if (file.size === 0) return 'That file is empty.'
  if (file.size > MAXIMUM_MARK_BYTES) return `A mark is at most ${MAXIMUM_MARK_BYTES / 1024} KB.`
  return null
}

/** The longest a single DNS label may be. The server refuses the same length. */
export const MAXIMUM_LABEL_LENGTH = 63

/**
 * One DNS label, or nothing.
 *
 * A subdomain of this deployment's own zone rather than a host of the organization's choosing, and
 * that is a deliberate narrowing of what this field used to take. Nothing in this product can check
 * that an organization controls `console.acme.com`: there is no DNS proof, no CNAME check, and the
 * only thing standing between two organizations claiming one host is a unique index and whoever
 * typed first. A label under a zone the deployment already serves needs no such proof, because the
 * deployment is the thing that answers for it.
 *
 * The server refuses the same shapes and is the one that decides; this exists to say so before a
 * round trip rather than to enforce anything. A browser is not a place to enforce authority.
 */
export function domainProblem(value: string): string | null {
  const label = value.trim().toLowerCase()
  if (!label) return null
  if (label.includes('/') || label.includes(':')) return 'Just the subdomain, with no scheme or path.'
  if (/\s/.test(label)) return 'A subdomain has no spaces in it.'
  if (label.includes('.'))
    return 'A sign-in domain is one label, such as acme. The rest of the host is this deployment’s.'
  if (label.length > MAXIMUM_LABEL_LENGTH)
    return `A subdomain is at most ${MAXIMUM_LABEL_LENGTH} characters. That one is ${label.length}.`
  if (!/^[a-z0-9-]+$/.test(label)) return 'A subdomain is letters, digits and hyphens.'
  if (label.startsWith('-') || label.endsWith('-')) return 'A subdomain does not begin or end with a hyphen.'
  return null
}

/**
 * The rest of the host, for a field that only takes the first label.
 *
 * Read off the window the operator is already looking at rather than configured, so it is true by
 * construction on any deployment. A host with nothing after its first label yields null and the
 * screen says nothing rather than showing a bare dot - which is the case on `localhost`, where there
 * is no zone to put an organization under.
 */
export function deploymentZone(hostname: string): string | null {
  const rest = hostname.trim().toLowerCase().split('.').slice(1).join('.')
  return rest || null
}

export function footerProblem(value: string): string | null {
  const length = value.trim().length
  return length > MAXIMUM_FOOTER_LENGTH
    ? `A report footer is at most ${MAXIMUM_FOOTER_LENGTH} characters. That one is ${length}.`
    : null
}

/**
 * Whether the swatches this console offers are still the swatches the server accepts.
 *
 * The list above is a copy, and a copy that can drift is only acceptable if something notices. The
 * screen calls this with the server's own list and says so rather than letting an operator pick a
 * color that is then refused - or, worse, letting a color the server has started accepting go
 * unoffered and unmeasured.
 */
export function swatchDrift(offeredByServer: readonly string[]): string | null {
  const ours = BRAND_SWATCHES.map((swatch) => swatch.value)
  const theirs = offeredByServer.map((value) => value.trim().toLowerCase())

  const missing = theirs.filter((value) => !ours.includes(value))
  const extra = ours.filter((value) => !theirs.includes(value))
  if (missing.length === 0 && extra.length === 0) return null

  return [
    'The colors this console offers are not the colors the server accepts.',
    missing.length > 0 ? `It accepts ${missing.join(', ')}, which this console does not offer.` : null,
    extra.length > 0 ? `This console offers ${extra.join(', ')}, which it would refuse.` : null
  ]
    .filter(Boolean)
    .join(' ')
}
